import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { Product, ProductType } from '~redux/brand/types';
import { buyerInfoSelector } from '~redux/buyer/selectors';
import { clearCartProducts } from '~redux/cart/actions';
import { cartProductsSelector } from '~redux/cart/selectors';
import {
  addCartAll,
  getCartByProduct,
  updateCart as updateCartThunk,
} from '~redux/cart/thunk';
import { useThunk } from '~utils/hooks';

export const useCart = (productId: string, isCart?: boolean) => {
  const [addCart, loading, complete] = useThunk(addCartAll);
  const user = useSelector(authUserSelector);
  const buyerInfo = useSelector(buyerInfoSelector);
  const addToCart = useCallback(
    async ({
      product,
      productTypes,
      quantities,
      isDirect,
    }: {
      product: Product;
      productTypes?: ProductType[];
      quantities: Record<string, number>;
      isDirect?: boolean;
    }) => {
      if (!user || !buyerInfo) {
        return;
      }
      await addCart(
        buyerInfo,
        Object.entries(quantities)
          .filter(([, quantity]) => !!quantity)
          .map(([key, quantity]) => ({
            quantity,
            owner: user.attributes.sub,
            product_id: product.id!,
            product_type_id: productTypes?.length ? key : undefined,
            product,
            is_direct: isDirect,
            brand_id: product.product_brand_id!,
          })),
        user
      );
    },
    [user, buyerInfo]
  );

  const cartItems = useSelector(cartProductsSelector);
  const cartQuantities = useMemo<Record<string, number> | undefined>(() => {
    if (!cartItems?.length) {
      return;
    }
    return cartItems.reduce((prev, { productType, product_id, quantity }) => {
      const key = productType?.id ?? product_id;
      return { ...prev, [key]: quantity };
    }, {});
  }, [cartItems?.length]);

  const [updateCart, updating] = useThunk(updateCartThunk);
  const update = useCallback(
    async ({
      product,
      productTypes,
      quantities,
      isDirect,
    }: {
      product: Product;
      productTypes?: ProductType[];
      quantities: Record<string, number>;
      isDirect: boolean;
    }) => {
      if (!user || !buyerInfo || !product) {
        return;
      }
      const items = Object.entries(quantities).map(([key, quantity]) => {
        const cartItem = cartItems.find(
          (item) => item.product_id === key || item.product_type_id === key
        );
        return {
          id: cartItem?.id ?? '',
          quantity,
          owner: user.attributes.sub,
          product_id: product.id!,
          product_type_id: productTypes?.length ? key : undefined,
          product,
          is_direct: isDirect,
          brand_id: product.product_brand_id!,
        };
      });
      await updateCart(user, buyerInfo, items);
    },
    [buyerInfo, user, cartItems]
  );

  const dispatch = useDispatch();
  const [fetchCart] = useThunk(getCartByProduct);
  useEffect(() => {
    if (!user || !isCart) {
      return;
    }
    fetchCart(productId, user.attributes.sub);
    return () => {
      dispatch(clearCartProducts());
    };
  }, [isCart, user]);

  return {
    cartQuantities,
    addToCart,
    updateCart: update,
    isLoading: loading || updating,
    isComplete: complete,
  };
};
